import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import {
  italicVariants,
  lineClampVariants,
  textAlignVariants,
  toneVariants,
  truncateVariants,
  typographyWeightVariants,
} from "./shared";

const textVariants = cva("", {
  variants: {
    size: {
      lg: [
        "font-(family-name:--font-family-body)",
        "text-(length:--font-size-body-lg)",
        "leading-(--line-height-24)",
        "tracking-[0.5px]",
      ],
      md: [
        "font-(family-name:--font-family-body)",
        "text-(length:--font-size-body-md)",
        "leading-(--line-height-20)",
        "tracking-[0.25px]",
      ],
      sm: [
        "font-(family-name:--font-family-body)",
        "text-(length:--font-size-body-sm)",
        "leading-(--line-height-16)",
        "tracking-[0.4px]",
      ],
    },
    tone: toneVariants,
    align: textAlignVariants,
    truncate: truncateVariants,
    lineClamp: lineClampVariants,
    weight: typographyWeightVariants,
    italic: italicVariants,
  },
  defaultVariants: {
    size: "md",
    tone: "default",
    weight: "regular",
    italic: false,
  },
});

type TextElement = "p" | "span" | "div";

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className">,
    VariantProps<typeof textVariants> {
  /** Override the default HTML element (defaults to p) */
  as?: TextElement;
}

/**
 * Text component for body/paragraph content.
 * Uses Inter font family with regular weight.
 *
 * Sizes: large (16px), medium (14px), small (12px).
 */
function Text({
  size = "md",
  tone,
  align,
  truncate,
  lineClamp,
  weight = "regular",
  italic = false,
  as = "p",
  ...props
}: TextProps) {
  return React.createElement(as, {
    className: textVariants({
      size,
      tone,
      align,
      truncate,
      lineClamp,
      weight,
      italic,
    }),
    ...props,
  });
}

export { Text };
